import React, { useRef, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { measureTextWidth } from 'pxc-editor-material-svg-rendering/helpers/calculation';
import { generateFontString } from 'pxc-editor-material-svg-rendering/helpers/general';
import { ENDLESS_FIXED, ENDLESS_BASED_ON_CONTENT, USER_SIZE_UNIT_INCH } from '@/constants';
import { Tooltip } from '@/components';
import { getDisplayedSizeValueByUnit, getCalcSizeValueByUnit } from '@/helpers';
import { setProjectPrintingAreaSizeThunk } from '@/actions';
import IconRadioButton from '@/icons/icon-cmme-radio-button.svg';
import IconRadioButtonSelected from '@/icons/icon-cmme-radio-button-selected.svg';
import {
  Label,
  TooltipBodyWrapper,
  SubLabel,
  TooltipLengthInput,
  TooltipRowWithInput,
  TooltipRowWrapper,
  Icon,
} from './styles';

const CONTROL_PANEL_WIDTH = 26;
const PANEL_HEIGHT = 26;
const HOR_OFFSET = 25;
const VERT_OFFSET = -PANEL_HEIGHT / 2;
const FONT_SIZE = 14;
const RX = PANEL_HEIGHT / 2;
const RY = RX;
const LABEL_OFFSET = 10;
const INFINITE_ICON_WIDTH = 46;

const borderRectStyle = {
  fill: 'transparent',
  cursor: 'pointer'
};

const getDisplayedWidth = (box, unit) => getDisplayedSizeValueByUnit(Math.abs(box.r - box.l), unit);

const getParsedValue = (value) => parseFloat(value) || 0;

const CmmePageLengthControl = (props) => {
  const {
    pageX,
    pageY,
    buildPageLengthLabelHelper,
    labelOnly,
    userSizeUnit,
    selectedPage,
  } = props;
  const { uid, box, resizeType, endless } = selectedPage;
  const pageWidth = box.r - box.l;
  const startX = pageX + HOR_OFFSET;
  const startY = pageY + VERT_OFFSET;
  const labelVal = labelOnly ? buildPageLengthLabelHelper(Number((pageWidth).toFixed(3)), ENDLESS_FIXED) : buildPageLengthLabelHelper(Number((pageWidth).toFixed(3)), resizeType);
  const labelValWidth = labelVal === Infinity && !labelOnly ? INFINITE_ICON_WIDTH : measureTextWidth(labelVal, generateFontString(false, false, FONT_SIZE, 'Arial'));
  const infoPanelWidth = labelValWidth + LABEL_OFFSET * 2; 
  const [endlessResizeType, setResizeType] = useState(resizeType);
  const [inputValue, setInputValue] = useState(getDisplayedWidth(box, userSizeUnit));
  const [inputError, setInputError] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (resizeType !== endlessResizeType) {
      setResizeType(resizeType);
    }
  }, [resizeType]);
  useEffect(() => {
    if (pageWidth !== inputValue) {
      setInputValue(pageWidth);
    }
  }, [pageWidth]);

  const isInputValid = value => {
    if (endless) {
      return value >= endless.length.minimum && value <= endless.length.maximum;
    }
    return value >= 0 && value <= 3000;
  };

  const handleApplyClick = () => {
    const isResizeTypeNotEqual = endlessResizeType !== resizeType;
    const isInputValueNotEqual = inputValue !== pageWidth;
    
    if (isResizeTypeNotEqual || isInputValueNotEqual) {
      switch (endlessResizeType) {
        case ENDLESS_BASED_ON_CONTENT: {
          dispatch(setProjectPrintingAreaSizeThunk({ uid, type: endlessResizeType, length: -1 }));
          break;
        }
        case ENDLESS_FIXED: {
          const value = parseFloat(inputValue) || 0;
          const calcSizeValueByUnit = getCalcSizeValueByUnit(value, userSizeUnit);
          const valid = isInputValid(calcSizeValueByUnit);

          if (valid) {
            dispatch(setProjectPrintingAreaSizeThunk({ uid, type: endlessResizeType, length: calcSizeValueByUnit }));
          } else {
            setInputError(true);
          }

          break;
        }
        default: {
          break;
        }
      }
    }

    setOpen(false);
  };

  const handleResizeTypeChange = type => () => {
    setResizeType(type);
  };

  const handleChange = e => {
    const isValid = /^\d*([,.][0-9]*?)?$/.test(e.target.value);

    if (isValid) {
      const value = e.target.value.replace(",", ".");
      const parsedValue = getParsedValue(value);
      setInputValue(value);
      setInputError(!isInputValid(getCalcSizeValueByUnit(parsedValue, userSizeUnit)));
    }
  }

  return (
    <g id="page-length-control">
      <rect
        x={startX}
        y={startY}
        width={infoPanelWidth + (labelOnly ? 0 : CONTROL_PANEL_WIDTH)}
        height={PANEL_HEIGHT}
        rx={RX}
        ry={RY}
        fill="white"
        stroke="black"
      />
      <rect
        x={startX}
        y={startY}
        width={infoPanelWidth}
        height={PANEL_HEIGHT}
        fill="transparent"
      />
      {labelVal !== Infinity || labelOnly ? (
        <text
          x={startX + infoPanelWidth / 2}
          y={pageY}
          alignmentBaseline="central"
          textAnchor="middle"
          fontSize={FONT_SIZE}
        >{labelVal}</text>
      ) : (
        <svg
          x={startX + infoPanelWidth / 2 - 20}
          y={startY + ((PANEL_HEIGHT - 15) / 2)}
          xmlns="http://www.w3.org/2000/svg"
          width={INFINITE_ICON_WIDTH}
          height="15"
          viewBox="0 0 27 13"
        >
          <path
            fill="#1D1D1B"
            fillRule="evenodd"
            d="M20.546 0c-1.2 0-2.578.636-4.097 1.89-1.202.992-2.27 2.184-2.955 3.01l-.485.586.018.018-.81 1.026-.168.203c-.373.453-.96 1.134-1.64 1.809-1.575 1.563-3.016 2.46-3.955 2.46-2.464 0-4.47-2.02-4.47-4.502 0-2.482 2.006-4.501 4.47-4.501.634 0 1.514.415 2.501 1.173L7.721 4.92l4.232-.572L11.197 0l-1.09 1.542c-.254-.194-.503-.37-.745-.526C8.312.342 7.335 0 6.454 0 2.895 0 0 2.916 0 6.5S2.895 13 6.454 13c1.22 0 2.623-.656 4.171-1.95 1.21-1.013 2.279-2.217 2.962-3.049l.393-.502.262-.336.088-.113c.013-.016.025-.032.036-.049h.001c.24-.316 1.112-1.438 2.221-2.54C18.164 2.896 19.606 2 20.546 2c2.464 0 4.47 2.019 4.47 4.501s-2.006 4.501-4.47 4.501c-.65 0-1.557-.435-2.575-1.23l1.195-1.69-4.232.572L15.69 13l1.13-1.598c.279.216.553.41.818.581 1.05.675 2.027 1.017 2.908 1.017C24.105 13 27 10.084 27 6.5S24.105 0 20.546 0"
          />
        </svg>
      )}

      {!labelOnly && (
        <>
          <line
            x1={startX + infoPanelWidth}
            y1={startY}
            x2={startX + infoPanelWidth}
            y2={pageY - VERT_OFFSET}
            stroke="black"
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            x={startX + infoPanelWidth + 4}
            y={startY + 5}
            width={CONTROL_PANEL_WIDTH}
            height={PANEL_HEIGHT}
            viewBox="0 0 25 25"
          >
            <path
              fillRule="evenodd"
              d="M12.6 13.067c.258 0 .467.209.467.466 0 .258-.21.467-.467.467H.467C.209 14 0 13.791 0 13.533c0-.257.209-.466.467-.466H12.6zM11.7 0c.54 0 1.044.222 1.503.662.52.499.79 1.036.797 1.59.01.565-.248 1.122-.765 1.657-.501.518-6.687 6.62-7.009 6.937-.052.049-.11.084-.179.104-.508.153-3.592 1.154-3.625 1.164-.042.013-.088.02-.13.02-.11 0-.218-.043-.3-.125-.113-.114-.152-.28-.107-.433.01-.033.924-3.029 1.107-3.703.02-.072.058-.137.11-.193 0 0 6.183-6.182 6.908-6.904l.062-.062C10.238.548 10.853 0 11.7 0zm.002 1.017c-.478 0-.868.375-.91.417-.192.19-4.549 4.546-6.275 6.273l-.585.586c-.146.515-.472 1.604-.745 2.507.846-.274 1.965-.632 2.434-.78l.312-.309c4.08-4.026 6.293-6.216 6.576-6.51.323-.336.482-.645.482-.929-.007-.274-.17-.567-.488-.874-.267-.257-.527-.38-.8-.38z"
            />
          </svg>
          <Tooltip className='Select inputContainer' placement="bottom" open={open} handleClose={() => handleApplyClick()} title=
            {
              <TooltipBodyWrapper>
                <Label>
                  <FormattedMessage
                    id="app.editor.modal.sizeOfPrintingArea"
                    defaultMessage="Size of printing area"
                  />:
                </Label>
                <TooltipRowWrapper>
                  <Icon src={endlessResizeType === ENDLESS_BASED_ON_CONTENT ? IconRadioButtonSelected : IconRadioButton} onClick={handleResizeTypeChange(ENDLESS_BASED_ON_CONTENT)} />
                  <SubLabel onClick={handleResizeTypeChange(ENDLESS_BASED_ON_CONTENT)}>
                    <FormattedMessage
                      id="app.editor.modal.pageLength.basedOnContent"
                      defaultMessage="Based on content"
                    />
                  </SubLabel>
                </TooltipRowWrapper>
                <TooltipRowWithInput>
                  <Icon src={endlessResizeType === ENDLESS_FIXED ? IconRadioButtonSelected : IconRadioButton} onClick={handleResizeTypeChange(ENDLESS_FIXED)} />
                  <SubLabel onClick={handleResizeTypeChange(ENDLESS_FIXED)}>
                    <FormattedMessage
                      id="app.editor.modal.pageLength.fixed"
                      defaultMessage="Fixed"
                    /> (
                    <FormattedMessage id={`app.sizeUnits.${userSizeUnit}`} defaultMessage={userSizeUnit} />
                    )
                  </SubLabel>
                  <TooltipLengthInput
                    $error={inputError}
                    ref={inputRef}
                    type="text"
                    step={userSizeUnit === USER_SIZE_UNIT_INCH ? 0.1 : 1}
                    value={inputValue}
                    disabled={endlessResizeType !== ENDLESS_FIXED}
                    onKeyUp={(e) => e.code === 'Enter' ? handleApplyClick() : null}
                    onChange={handleChange}
                    onFocus={() => {
                      if (inputRef.current) {
                        inputRef.current.select();
                      }
                    }}
                  />
                </TooltipRowWithInput>
              </TooltipBodyWrapper>
            }
          >
            <rect
              x={startX + infoPanelWidth}
              y={startY}
              width={CONTROL_PANEL_WIDTH}
              height={PANEL_HEIGHT}
              style={borderRectStyle}
              onClick={() => setOpen(true)}
              onTouchEnd={() => setOpen(true)}
            />
          </Tooltip>
        </>
      )}
    </g>
  );
};

export default React.memo(CmmePageLengthControl);
